<script setup lang="ts">
import defu from 'defu'
import { defineEmits, ref, watch } from 'vue'

interface JawPose {
  bias: number[]
  weight: number
}

interface EyeBlink {
  left: number
  right: number
}

interface NeckPose {
  bias: number[]
  weight: number[]
}

interface Expression {
  weight: number
}

interface Settings {
  jaw_pose: JawPose
  rotation: number[]
  eye_blink: EyeBlink
  neck_pose: NeckPose
  expression: Expression
}

const defaultSchema: Settings = {
  jaw_pose: { bias: [0, 0, 0], weight: 1 },
  rotation: [0, 0, 0],
  eye_blink: { left: 0, right: 0 },
  neck_pose: { bias: [0, 0, 0], weight: [1, 1, 1] },
  expression: { weight: 1 },
}

const settingsJSON = defineModel<string>('settings', { required: true })
const settings = ref<Settings>(defaultSchema)

// When the modelValue changes, parse it and update settings
watch(() => settingsJSON.value, (newValue) => {
  console.log('settingsJSON.value', newValue)

  if (typeof newValue === 'object') {
    if (Object.keys(newValue).length === 0)
      return

    settings.value = newValue
    console.log('settings.value', settings.value)
    return
  }

  if (typeof newValue === 'string' && !newValue.startsWith('{'))
    return

  try {
    settings.value = JSON.parse(newValue)
  }
  catch (e) {
    console.error('Invalid JSON in model settings', e)
    // Keep using current settings if JSON is invalid
  }
}, { immediate: true })
</script>

<template>
  <div class="settings-editor">
    <el-collapse>
      <!-- Jaw Pose -->
      <el-collapse-item title="下颚姿势 (Jaw Pose)" name="jawPose">
        <div class="nested-setting">
          <h4>偏移量 (Bias)</h4>
          <div class="array-inputs">
            <el-form-item v-for="(_, index) in 3" :key="index">
              <el-input-number v-model="settings.jaw_pose.bias[index]" type="text" />
            </el-form-item>
          </div>

          <h4>权重 (Weight)</h4>
          <el-form-item class="array-inputs">
            <el-input-number v-model="settings.jaw_pose.weight" type="text" />
          </el-form-item>
        </div>
      </el-collapse-item>

      <!-- Rotation -->
      <el-collapse-item title="旋转 (Rotation)" name="rotation">
        <div class="array-inputs">
          <el-form-item v-for="(_, index) in 3" :key="index">
            <el-input-number v-model="settings.rotation[index]" type="text" />
          </el-form-item>
        </div>
      </el-collapse-item>

      <!-- Eye Blink -->
      <el-collapse-item title="眨眼 (Eye Blink)" name="eyeBlink">
        <div class="nested-setting">
          <div class="input-row">
            <span>左眼 (Left):</span>
            <el-form-item>
              <el-input-number v-model="settings.eye_blink.left" type="text" />
            </el-form-item>
          </div>
          <div class="input-row">
            <span>右眼 (Right):</span>
            <el-form-item>
              <el-input-number v-model="settings.eye_blink.right" type="text" />
            </el-form-item>
          </div>
        </div>
      </el-collapse-item>

      <!-- Neck Pose -->
      <el-collapse-item title="颈部姿势 (Neck Pose)" name="neckPose">
        <div class="nested-setting">
          <h4>偏移量 (Bias)</h4>
          <div class="array-inputs">
            <el-form-item v-for="(_, index) in 3" :key="index">
              <el-input-number v-model="settings.neck_pose.bias[index]" type="text" />
            </el-form-item>
          </div>

          <h4>权重 (Weight)</h4>
          <div class="array-inputs">
            <el-form-item v-for="(_, index) in 3" :key="index">
              <el-input-number v-model="settings.neck_pose.weight[index]" type="text" />
            </el-form-item>
          </div>
        </div>
      </el-collapse-item>

      <!-- Expression -->
      <el-collapse-item title="表情 (Expression)" name="expression">
        <div class="nested-setting">
          <div class="input-row">
            <span>权重 (Weight):</span>
            <el-form-item>
              <el-input-number v-model="settings.expression.weight" type="text" />
            </el-form-item>
          </div>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<style scoped>
.settings-editor {
  width: 100%;
  text-align: left;
  padding: 0 8px;
}

:deep(.el-collapse-item) {
  margin-bottom: 8px;
  padding-bottom: 8px;
}

:deep(.el-collapse-item:last-child) {
  border-bottom: none;
}

:deep(.el-collapse-item__content) {
  padding: 8px 12px 12px;
}

:deep(.el-collapse-item__header) {
  padding: 0 8px;
  font-weight: 500;
}

.nested-setting {
  padding: 0;
  text-align: left;
  margin: 0 0 12px 0;
}

:deep(.el-form-item) {
  margin-bottom: 12px;
}

.array-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
}

.array-inputs .el-input-number {
  width: 100%;
  max-width: 300px;
}

.input-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 12px;
}

.input-row span {
  margin-bottom: 4px;
  font-size: 14px;
  color: #606266;
}

.input-row .el-input-number {
  width: 100%;
  max-width: 300px;
}

h4 {
  margin: 16px 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
}
</style>
